import { Alert, Badge, Button, Code, Group, Menu, Modal, Paper, Select, Skeleton, Stack, Table, Text, TextInput, VisuallyHidden, type MantineColor } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconDownload, IconKey, IconPlus, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { KeyState } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import type { Key } from '@motionsafe/ms-console-api/admin/v1/key_pb.js';

import { api } from '../../api/client';
import { ActionsTrigger, MenuAction } from '../../components/actions';
import { errorText, keysInfoQuery, meQuery, orgQuery, useInvalidate } from '../../api/hooks';
import { CopyKey } from '../../components/copy-key';
import { useDialog } from '../../components/dialog';
import { useGridRows } from '../../components/grid';
import { usePages } from '../../components/pager';
import { locked, LockedActions } from '../../components/locked';
import { EmptyState, PageHeader, ToolbarActions, type StatProps } from '../../components/page';
import { fraction, when } from '../../lib/format';
import { isStaff } from '../../lib/roles';
import { ICON, ICON_INLINE } from '../../theme';
import { Route as AppRoute, useAccess, useOrgId } from '../_app';
import { DialogLoadBar } from '../../components/top-load-bar';

// MotionSafe staff only: an organization's administrators see nothing of its key.
export const Route = createFileRoute('/_app/keys')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQuery);
    if (!me || !isStaff(me)) throw redirect({ to: '/overview', search: {} });
  },
  component: Keys,
});

const COLS = 8;
// How a key held in the identity vendor's vault is named on the wire.
const VAULT = 'workos-vault';

// State words are the audit trail's words, verbatim. Field Green for current,
// the warm neutral for retired.
const tone = (s: KeyState): MantineColor => (s === KeyState.CURRENT ? 'success' : 'gray');
const word = (s: KeyState) => KeyState[s].toLowerCase();

type StateFilter = 'all' | 'current' | 'retired';

// The locked control: an org admin, and read-only staff, see every key action
// at ink-secondary and inert, with the caption naming who can change it.
const LOCKED_CAPTION = 'Managed by MotionSafe. Contact your account manager.';
const LOCKED_KEY_ACTIONS = [['Download escrow copy']];

// Placeholder widths per column, so the loading rows have the table's shape.
const SKELETON_WIDTHS = [56, 96, 72, '50%', 64, 136, 120, 72];

function Missing({ children }: { children: ReactNode }) {
  return <Text span inherit c="dimmed">{children}</Text>;
}

function Keys() {
  const orgId = useOrgId();
  const { me } = AppRoute.useRouteContext();
  // Keys are a MotionSafe admin's to change; everyone else sees them locked.
  const access = useAccess();
  const manage = isStaff(me) && access.may('CreateKey');
  const caption = isStaff(me) ? access.caption('CreateKey') : LOCKED_CAPTION;
  const keys = useQuery({ ...keysInfoQuery(orgId), select: (r) => r.keys });
  // Where the console keeps new keys, and which of these are held elsewhere.
  const store = useQuery({ ...keysInfoQuery(orgId), select: (r) => r.keyStore }).data;
  const inVault = store === VAULT;
  const inv = useInvalidate();
  const outside = (keys.data ?? []).filter((k) => k.heldIn !== store);
  const move = useMutation({
    mutationFn: () => api.key.moveKeys({ orgId }),
    onSuccess: (r) => { void inv('keys', 'org'); notifications.show({ message: r.moved === 1 ? '1 key moved to the vault.' : `${r.moved} keys moved to the vault.` }); },
    onError: (e) => notifications.show({ message: errorText(e), color: 'danger' }),
  });
  const org = useQuery(orgQuery(orgId));
  const [creating, setCreating] = useState(false);
  // The key whose escrow copy is asked for, kept while its dialog fades out.
  const [escrow, setEscrow] = useState<Key | null>(null);
  const escrowDialog = useDialog();
  const [q, setQ] = useState('');
  const [state, setState] = useState<StateFilter>('all');
  const lockedCaption = useId();

  const all = keys.data ?? [];
  const currentKey = all.find((k) => k.identity?.state === KeyState.CURRENT);
  const current = currentKey?.identity;
  const retired = all.filter((k) => k.identity?.state === KeyState.RETIRED).length;

  const needle = q.trim().toLowerCase();
  const rows = all.filter((k) => {
    const id = k.identity;
    return !!id && (state === 'all' || word(id.state) === state)
      && (needle === '' || id.generation.toLowerCase().includes(needle) || id.fingerprint.toLowerCase().includes(needle));
  });
  const clearFilters = () => { setQ(''); setState('all'); };
  // Keys have no detail pane; Enter or Space on a row moves to its actions.
  const pages = usePages(rows, 'keys');
  const gridRow = useGridRows(pages.shown.map((k) => k.identity!.generation));
  const toActions = (row: HTMLTableRowElement) => row.querySelector<HTMLButtonElement>('button')?.focus();

  const stats: StatProps[] = [
    {
      label: 'Current key',
      // Generation beside fingerprint, as a tablet's Settings screen shows them.
      value: current ? <><span className="id">{current.generation}</span> <Text span c="dimmed" aria-hidden>·</Text> <span className="id">{current.fingerprint}</span></> : 'none',
      hint: current ? "Compare with a tablet's Settings screen" : 'MotionSafe issues the first key',
      tone: current ? 'neutral' : 'bad',
    },
    {
      label: 'Devices on the current key',
      value: currentKey ? fraction(currentKey.devicesFetched, currentKey.devicesActive) : 'none',
      hint: currentKey ? 'Active devices it was last given to as their current key' : 'No key is current',
    },
    {
      label: 'Retired',
      value: retired,
      hint: 'Kept forever so every card ever sealed stays openable',
    },
  ];

  // Not until the keys are read: the dialog names the key it replaces, and
  // read as none it would offer a first key with no warning. Keys read once
  // and since failing to refresh still serve: the service refuses a create
  // that names a key no longer current.
  const loaded = keys.data !== undefined;
  const newKey = manage
    ? <Button size="sm" leftSection={<IconPlus {...ICON_INLINE} />} disabled={!loaded} onClick={() => setCreating(true)}>New key</Button>
    : <Button size="sm" variant="default" leftSection={<IconPlus {...ICON_INLINE} />} {...locked(lockedCaption)}>New key</Button>;

  return (
    <>
      <PageHeader
        title="Keys"
        description="The organization's external storage key and every key it has ever had."
        stats={keys.isPending ? 'loading' : keys.data ? stats : undefined}
        statsCount={3}
      />

      <Group align="center" gap="sm" mb="md" wrap="wrap">
        <TextInput
          aria-label="Search keys"
          placeholder="Search generation or fingerprint"
          leftSection={<IconSearch {...ICON_INLINE} />}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={{ base: '100%', xs: 320 }}
        />
        <Select aria-label="Filter by state" allowDeselect={false} w={160} value={state}
          data={[{ value: 'all', label: 'Any state' }, { value: 'current', label: 'current' }, { value: 'retired', label: 'retired' }]}
          onChange={(v) => setState((v ?? 'all') as StateFilter)} />
        <ToolbarActions note={manage ? undefined : caption} noteId={lockedCaption}>{newKey}</ToolbarActions>
      </Group>

      {manage && inVault && outside.length > 0 && (
        <Alert color="warning" mb="lg" role="status" icon={<IconAlertTriangle {...ICON} />} title={`${fraction(outside.length, all.length)} keys are held in the console's database`}>
          <Stack gap="sm" align="flex-start">
            <Text size="sm">They are wrapped under the console's master key and would be lost with this database. Moving them stores each one in the vault; the keys themselves do not change, and tablets notice nothing.</Text>
            <Button size="sm" variant="default" onClick={() => move.mutate()} disabled={move.isPending}>Move to vault</Button>
          </Stack>
        </Alert>
      )}

      <Paper p={0} style={{ overflow: 'hidden' }}>
        {keys.data && all.length === 0 ? (
          <EmptyState
            icon={IconKey}
            title="No key yet"
            body="MotionSafe issues the first key. Nobody can sign in on a device until one is current."
          />
        ) : keys.data && rows.length === 0 ? (
          <EmptyState
            icon={IconSearch}
            title="No key matches"
            body={needle ? `No key matches “${q.trim()}” in this state.` : 'No key is in this state.'}
            action={<Button variant="default" onClick={clearFilters}>Clear filters</Button>}
          />
        ) : (
          <Table.ScrollContainer minWidth={1060}>
            <Table role="grid" layout="fixed" aria-label="Keys of this organization" aria-busy={keys.isPending} highlightOnHover={rows.length > 0}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={120}>Generation</Table.Th><Table.Th>Fingerprint</Table.Th><Table.Th w={120}>State</Table.Th><Table.Th w={170}>Held in</Table.Th><Table.Th w={150}>Sealing under it</Table.Th><Table.Th w={190}>Created</Table.Th><Table.Th w={190}>Retired</Table.Th>
                  <Table.Th w={52}><VisuallyHidden>Actions</VisuallyHidden></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pages.shown.map((k) => (
                  <Table.Tr key={k.identity!.generation} {...gridRow(k.identity!.generation, toActions)}>
                    <Table.Td className="id">{k.identity!.generation}</Table.Td>
                    <Table.Td className="id">{k.identity!.fingerprint}</Table.Td>
                    <Table.Td><Badge color={tone(k.identity!.state)}>{word(k.identity!.state)}</Badge></Table.Td>
                    <Table.Td><Text span size="sm" {...(k.heldIn === VAULT ? {} : { c: 'dimmed' })}>{k.heldIn === VAULT ? 'vault' : 'console database'}</Text></Table.Td>
                    <Table.Td className="num">{fraction(k.devicesFetched, k.devicesActive)}</Table.Td>
                    <Table.Td className="num" style={{ whiteSpace: 'nowrap' }}>{when(k.createdAt)}</Table.Td>
                    <Table.Td className="num" style={{ whiteSpace: 'nowrap' }}>{k.retiredAt ? when(k.retiredAt) : <Missing>in use</Missing>}</Table.Td>
                    <Table.Td ta="right">
                      {manage ? <KeyActions k={k} onEscrow={() => { setEscrow(k); escrowDialog.open(); }} /> : <LockedActions compact label={`Actions for ${k.identity!.generation}`} caption={caption} groups={LOCKED_KEY_ACTIONS} />}
                    </Table.Td>
                  </Table.Tr>
                ))}
                {keys.isPending && [0, 1, 2].map((i) => (
                  <Table.Tr key={`loading-${i}`} bg="transparent">
                    {SKELETON_WIDTHS.map((w, j) => (
                      <Table.Td key={j} {...(j === COLS - 1 ? { ta: 'right' as const } : {})}>
                        {i === 0 && j === 0 && <VisuallyHidden>Loading keys</VisuallyHidden>}
                        {/* A row is as tall as its Actions button. */}
                        <Skeleton height={14} width={w} radius="sm" my={15} {...(j === COLS - 1 ? { ml: 'auto' } : {})} />
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
                {keys.isError && (
                  <Table.Tr>
                    <Table.Td colSpan={COLS}>
                      <Group gap="sm" justify="center" py="lg">
                        <Text size="sm" c="danger">{errorText(keys.error)}</Text>
                        <Button variant="subtle" size="compact-sm" onClick={() => void keys.refetch()} disabled={keys.isFetching}>Try again</Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {pages.footer}
      </Paper>
      {manage && loaded && <CreateKeyDialog opened={creating} onClose={() => setCreating(false)} orgId={orgId} orgSlug={org.data?.slug ?? ''} generations={all.map((k) => k.identity?.generation ?? '')} current={current?.generation ?? ''} />}
      {manage && escrow && escrowDialog.mounted && <EscrowDialog opened={escrowDialog.opened} round={escrowDialog.round} k={escrow} orgId={orgId} onClose={escrowDialog.close} />}
    </>
  );
}

// No key changes state from here: a new key replaces the current one as it is
// created, so the one action a key has is its escrow copy.
function KeyActions({ k, onEscrow }: { k: Key; onEscrow: () => void }) {
  const gen = k.identity!.generation;
  return (
    <Menu position="bottom-end">
      <Menu.Target>
        <ActionsTrigger label={`Actions for ${gen}`} />
      </Menu.Target>
      <Menu.Dropdown>
        <MenuAction icon={IconDownload} onClick={onEscrow}>Download escrow copy</MenuAction>
      </Menu.Dropdown>
    </Menu>
  );
}

// The generation a new key will take, as the service assigns it: one past
// the highest `v<number>` there is. Shown before it is asked for; the
// service decides when the key is made.
function nextGeneration(existing: string[]): string {
  let highest = 0;
  for (const g of existing) {
    const m = /^v(\d{1,15})$/.exec(g);
    if (m) highest = Math.max(highest, Number(m[1]));
  }
  return `v${highest + 1}`;
}

// What staff type before a key is made. Making one should be about the
// rarest thing anyone does here, and replacing the current key cannot be
// undone, so it is never a press away: the phrase is typed exactly, in
// capitals, and the dialog says first what the key sets in motion.
const GENERATE_PHRASE = 'GENERATE KEY';
const REPLACE_PHRASE = 'REPLACE KEY';
const saysPhrase = (typed: string, phrase: string) => typed.trim().replace(/\s+/g, ' ') === phrase;

function CreateKeyDialog({ opened, onClose, orgId, orgSlug, generations, current }: { opened: boolean; onClose: () => void; orgId: string; orgSlug: string; generations: string[]; current: string }) {
  const inv = useInvalidate();
  const [error, setError] = useState('');
  const [typed, setTyped] = useState('');
  const phraseId = useId();
  const gen = nextGeneration(generations);
  const org = orgSlug || 'this organization';
  const first = current === '';
  const phrase = first ? GENERATE_PHRASE : REPLACE_PHRASE;
  const create = useMutation({
    // No generation is sent: the service assigns it, so two people creating at
    // once cannot collide. The key this dialog names is sent as the one it
    // replaces: if another is current by now, nothing changes.
    mutationFn: () => api.key.createKey({ orgId, generation: '', replaces: current }),
    onSuccess: (r) => {
      void inv('keys', 'org');
      const made = r.key?.identity?.generation ?? gen;
      const retired = r.retired?.identity?.generation;
      notifications.show({ message: retired
        ? `${made} is current. ${retired} is retired and stays in escrow.`
        : `${made} is this organization's first key and is current now. People can sign in on devices.` });
      if (r.warning) notifications.show({ message: sentenceCase(r.warning), color: 'warning', autoClose: false });
      onClose();
      setError('');
      setTyped('');
    },
    // Read the keys again: the answer may be that another key is current now,
    // and the dialog then names that one.
    onError: (e) => { void inv('keys', 'org'); setError(errorText(e)); setTyped(''); },
  });
  const ready = saysPhrase(typed, phrase);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (create.isPending || !ready) return;
    setError('');
    create.mutate();
  };
  // Not while the key is being made: its answer belongs to this opening, not the next.
  const close = () => { if (create.isPending) return; setError(''); setTyped(''); onClose(); };
  return (
    <Modal opened={opened} onClose={close} title={first ? `Create ${gen} as the first key?` : `Replace ${current} with ${gen}?`}>
      <DialogLoadBar />
      <form onSubmit={submit}>
        <Stack gap="lg">
          {first ? (
            <Text size="sm" maw="60ch"><span className="id">{gen}</span> becomes the current key for {org} at once, and everything collected on its devices is sealed under it from then on. It is kept for as long as the organization exists, so that what it sealed stays openable.</Text>
          ) : (
            <>
              <Alert color="warning" role="status" icon={<IconAlertTriangle {...ICON} />} title="This cannot be undone">
                <Text size="sm"><span className="id">{current}</span> is retired the moment <span className="id">{gen}</span> is created, and a retired key never becomes current again.</Text>
              </Alert>
              <Text size="sm" maw="60ch">Each of {org}'s devices seals new collections under <span className="id">{gen}</span> from its next renewal, within 15 minutes while it is online. <span className="id">{current}</span> stays in escrow, and a device fetches it the first time it opens a collection sealed under it. Until it connects, a device opens only what was sealed under the key it holds.</Text>
            </>
          )}
          <Text size="sm" c="dimmed" maw="60ch">Recorded in the audit trail under your name.</Text>

          <TextInput
            label={<>Type <Text span inherit fw={700}>{phrase}</Text> to confirm</>}
            aria-describedby={phraseId}
            value={typed}
            onChange={(ev) => setTyped(ev.currentTarget.value)}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            data-autofocus
          />
          <VisuallyHidden id={phraseId}>{first ? 'Generate key' : 'Replace key'} stays unavailable until the phrase is typed.</VisuallyHidden>

          {error && (
            <Alert color="danger" role="alert" icon={<IconAlertTriangle {...ICON} />} title="No key was created">
              <Text size="sm">{error === 'internal error'
                ? 'The service failed while creating the key, and nothing was stored. Try again; if it keeps failing, tell MotionSafe engineering that key creation answers with an internal error.'
                : sentenceCase(error)}</Text>
            </Alert>
          )}

          <Group className="dialog-footer" justify="flex-end" gap="sm">
            <Button variant="default" onClick={close} disabled={create.isPending}>Cancel</Button>
            <Button type="submit" {...(first ? {} : { color: 'danger' })} disabled={!ready || create.isPending}>{first ? 'Generate key' : 'Replace key'}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

// The service's sentence, as a sentence.
function sentenceCase(text: string): string {
  const t = text.trim();
  if (!t) return t;
  const first = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(first) ? first : `${first}.`;
}

type EscrowProps = { k: Key; orgId: string; onClose: () => void };

// The key material lives in the form, which is keyed by the opening and
// unmounted once the dialog has faded out, so it is not kept after Done.
function EscrowDialog({ opened, round, ...props }: EscrowProps & { opened: boolean; round: number }) {
  return (
    <Modal opened={opened} onClose={props.onClose} title={<>Escrow copy of <span className="id">{props.k.identity!.generation}</span></>}>
      <DialogLoadBar />
      <EscrowForm key={round} {...props} />
    </Modal>
  );
}

function EscrowForm({ k, orgId, onClose }: EscrowProps) {
  const gen = k.identity!.generation;
  const [reason, setReason] = useState('');
  const [key, setKey] = useState('');
  const download = useMutation({ mutationFn: () => api.key.downloadEscrow({ orgId, generation: gen, reason }), onSuccess: (r) => setKey(r.key), onError: (e) => notifications.show({ message: errorText(e), color: 'danger' }) });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim() && !download.isPending) download.mutate();
  };
  return (
    !key ? (
      <form onSubmit={submit}>
        <Stack gap="lg">
          <Alert color="danger" role="status" icon={<IconAlertTriangle {...ICON} />}>This shows the key material once. It is recorded in the audit trail with your name and the reason.</Alert>
          <TextInput label="Reason" withAsterisk description="Why an offline copy is needed now." value={reason} onChange={(e) => setReason(e.currentTarget.value)} data-autofocus />
          <Group className="dialog-footer" justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!reason.trim() || download.isPending}>Show key</Button>
          </Group>
        </Stack>
      </form>
    ) : (
      <Stack gap="lg">
        <Text size="sm">Store this outside the console. It is the only way to open cards sealed under <span className="id">{gen}</span> if the console is unavailable.</Text>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Code block className="id" flex={1} miw={0} style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{key}</Code>
          <CopyKey value={key} label="Copy escrow key" />
        </Group>
        <Group className="dialog-footer" justify="flex-end" gap="sm">
          <Button onClick={onClose}>Done</Button>
        </Group>
      </Stack>
    )
  );
}
